import api from '@/shared/utils/apiConfig';
import type { TreatmentPlanItem } from '@/modules/treatment-plan-item/api/treatmentPlanItem.api';
import type { Payment } from '@/modules/payment/api/payment.api';

export type TreatmentPlanPaymentStatus = 'unpaid' | 'partially paid' | 'paid';
export type TreatmentPlanConfirmationStatus = 'unconfirmed' | 'partial' | 'confirmed' | 'rejected';
export type TreatmentPlanCustomerClass = 'new' | 'returning_consult';

export interface DiscountAllocationSummary {
  id: string;
  treatmentPlanItemId: string;
  allocatedAmount: number;
  status?: 'active' | 'reversed';
}

export interface DiscountApplicationSummary {
  id: string;
  treatmentPlanId: string;
  sequence: number;
  scope: 'item' | 'plan';
  discountType: 'fixed' | 'percent';
  enteredValue: number;
  computedAmount: number;
  source: 'manual' | 'promotion' | 'voucher';
  promotionId?: string;
  reason?: string;
  targetItemIds: string[];
  status: 'active' | 'reversed';
  allocations: DiscountAllocationSummary[];
  appliedAt: string;
}

export interface TreatmentPlanStaff {
  id: string;
  name: string;
  specialization?: string;
  designation?: string;
  status: string;
}

export interface TreatmentPlan {
  id: string;
  patientId: string;
  /** Business patient code (e.g. BN00001), not the Mongo id. */
  patientCode?: string;
  patientName: string;
  planCode?: string;
  name: string;
  description: string;
  items: TreatmentPlanItem[];
  /** Σ item gross (unitPrice × qty). */
  planGrossAmount?: number;
  /** Σ active discount allocations. */
  planDiscountAmount?: number;
  /** Σ item.payableAmount — contracted patient due. */
  planPayableAmount?: number;
  planQuotedGrossAmount?: number;
  planQuotedPayableAmount?: number;
  unallocatedPlanAdvance?: number;
  /** @deprecated Alias of planGrossAmount */
  subtotalAmount: number;
  /** @deprecated Alias of planPayableAmount (not COGS) */
  totalCost: number;
  /** @deprecated PLAN-scope only; prefer planDiscountAmount for total */
  discountAmount: number;
  itemDiscountTotal?: number;
  planDiscountTotal?: number;
  planDiscountApplied: number;
  planDiscountRemaining: number;
  amountPaid: number;
  balanceAmount: number;
  paymentStatus: TreatmentPlanPaymentStatus;
  confirmationStatus?: TreatmentPlanConfirmationStatus;
  status?: string;
  rejectionReason?: string;
  customerClass?: TreatmentPlanCustomerClass;
  pricingModel?: 'legacy' | 'allocation';
  discountApplications?: DiscountApplicationSummary[];
  promotionId?: string;
  promotionName?: string;
  representativeDoctor?: TreatmentPlanStaff;
  consultant?: TreatmentPlanStaff;
  consultingStaff?: TreatmentPlanStaff;
  /** Calendar date YYYY-MM-DD. Distinct from system createdAt. */
  planCreatedOn?: string;
  createdAt?: string;
  updatedAt?: string;
  payments?: Payment[];
}

/** Payable plan row for the record-payment picker. No line items. */
export interface TreatmentPlanPaymentPickerRow {
  id: string;
  patientId: string;
  patientName: string;
  planCode?: string;
  name: string;
  balanceAmount: number;
  maxPlanAdvanceAmount: number;
  amountPaid: number;
  planPayableAmount: number;
  planQuotedPayableAmount: number;
  unallocatedPlanAdvance: number;
  representativeDoctor?: Pick<TreatmentPlanStaff, 'id' | 'name'>;
}

export interface TreatmentPlanPayload {
  patientId: string;
  name: string;
  /** Calendar date YYYY-MM-DD. */
  planCreatedOn?: string;
  description?: string;
  representativeDoctorId?: string | null;
  consultantId?: string | null;
  consultingStaffId?: string | null;
  promotionId?: string | null;
  customerClass?: TreatmentPlanCustomerClass;
  planDiscount?: {
    discountType: 'fixed' | 'percent';
    enteredValue: number;
    reason?: string;
  } | null;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
  message?: string;
}

export const treatmentPlanAPI = {
  getAll: async (params: Record<string, string> = {}): Promise<ApiResponse<TreatmentPlan[]>> => {
    const response = await api.get('/treatment-plans', { params });
    return response.data;
  },

  getPaymentPicker: async (params: {
    patientId?: string;
    purpose: 'service' | 'plan_advance' | 'plan_advance_refund';
  }): Promise<ApiResponse<TreatmentPlanPaymentPickerRow[]>> => {
    const response = await api.get('/treatment-plans', {
      params: {
        view: 'payment-picker',
        purpose: params.purpose,
        ...(params.patientId ? { patientId: params.patientId } : {}),
      },
    });
    return response.data;
  },

  getById: async (id: string, includePayments = false): Promise<ApiResponse<TreatmentPlan>> => {
    const response = await api.get(`/treatment-plans/${id}`, {
      params: includePayments ? { includePayments: 'true' } : undefined,
    });
    return response.data;
  },

  create: async (payload: TreatmentPlanPayload): Promise<ApiResponse<TreatmentPlan>> => {
    const response = await api.post('/treatment-plans', payload);
    return response.data;
  },

  update: async (id: string, payload: Partial<TreatmentPlanPayload>): Promise<ApiResponse<TreatmentPlan>> => {
    const response = await api.put(`/treatment-plans/${id}`, payload);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse> => {
    const response = await api.delete(`/treatment-plans/${id}`);
    return response.data;
  },

  reject: async (id: string, reason: string): Promise<ApiResponse<TreatmentPlan>> => {
    const response = await api.post(`/treatment-plans/${id}/reject`, { reason });
    return response.data;
  },
};
